<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { deriveSockDimensions, type FixedMeasurementKey, type FixedMeasurements } from '../sockMeasurements'
import SockMeasurementDrawing from './SockMeasurementDrawing.vue'

type Field = {
  key: FixedMeasurementKey
  code: string
  labelKey: string
  measurementKey: string
  toleranceKey: string
}

const props = defineProps<{
  fixedMeasurements: FixedMeasurements
  sockType?: string
  /** Preview/detail pages render the same table without editable inputs. */
  readonly?: boolean
}>()

const { t } = useI18n()

/**
 * Default finished dimensions of a sock, identical for every sock type:
 * A Tovondan rezinkaga, C Tovondan uchiga, D Rezina eni, E Eni, G Rezinka boyi.
 */
const defaultFields: Field[] = [
  { key: 'tovondan_rezinkaga_sm', code: 'A', labelKey: 'engineering.passportStructure.dimensionField.leg_height_mm.label', measurementKey: 'engineering.passportStructure.dimensionField.leg_height_mm.measurement', toleranceKey: 'engineering.passportStructure.dimensionField.leg_height_mm.tolerance' },
  { key: 'tovondan_uchiga_sm', code: 'C', labelKey: 'engineering.passportStructure.dimensionField.foot_length_mm.label', measurementKey: 'engineering.passportStructure.dimensionField.foot_length_mm.measurement', toleranceKey: 'engineering.passportStructure.dimensionField.foot_length_mm.tolerance' },
  { key: 'rezinka_eni_sm', code: 'D', labelKey: 'engineering.passportStructure.dimensionField.cuff_width_mm.label', measurementKey: 'engineering.passportStructure.dimensionField.cuff_width_mm.measurement', toleranceKey: 'engineering.passportStructure.dimensionField.cuff_width_mm.tolerance' },
  { key: 'eni_sm', code: 'E', labelKey: 'engineering.passportStructure.dimensionField.foot_width_mm.label', measurementKey: 'engineering.passportStructure.dimensionField.foot_width_mm.measurement', toleranceKey: 'engineering.passportStructure.dimensionField.foot_width_mm.tolerance' },
  { key: 'rezinka_boyi_sm', code: 'G', labelKey: 'engineering.passportStructure.dimensionField.cuff_height_mm.label', measurementKey: 'engineering.passportStructure.dimensionField.cuff_height_mm.measurement', toleranceKey: 'engineering.passportStructure.dimensionField.cuff_height_mm.tolerance' },
]

/** Fabric-density sample: not a sock dimension, so it only shows when a passport already stores it. */
const densityFields: Field[] = [
  { key: 'eni_plotnost_sm', code: 'I', labelKey: 'engineering.passportStructure.dimensionField.eni_plotnost_sm.label', measurementKey: 'engineering.passportStructure.dimensionField.eni_plotnost_sm.measurement', toleranceKey: 'engineering.passportStructure.dimensionField.eni_plotnost_sm.tolerance' },
  { key: 'uzunligi_plotnost_sm', code: 'J', labelKey: 'engineering.passportStructure.dimensionField.uzunligi_plotnost_sm.label', measurementKey: 'engineering.passportStructure.dimensionField.uzunligi_plotnost_sm.measurement', toleranceKey: 'engineering.passportStructure.dimensionField.uzunligi_plotnost_sm.tolerance' },
]

function toRow(field: Field) {
  return {
    ...field,
    label: t(field.labelKey),
    measurement: t(field.measurementKey),
    tolerance: t(field.toleranceKey),
    value: props.fixedMeasurements[field.key] ?? null,
  }
}

/** The letter-coded dimension form is the same for every sock length. */
const rows = computed(() => [
  ...defaultFields.map(toRow),
  ...densityFields
    .filter((field) => Number(props.fixedMeasurements[field.key] ?? 0) > 0)
    .map(toRow),
])

const dimensions = computed(() => deriveSockDimensions(props.fixedMeasurements))

const derived = computed(() => [
  { key: 'toe_length_mm', label: t('engineering.passportStructure.dimensionField.toe_length_mm.label'), value: dimensions.value.toe_length_mm },
  { key: 'heel_length_mm', label: t('engineering.passportStructure.dimensionField.heel_length_mm.label'), value: dimensions.value.heel_length_mm },
  { key: 'instep_length_mm', label: t('engineering.passportStructure.dimensionField.instep_length_mm.label'), value: dimensions.value.instep_length_mm },
].filter((item) => item.value !== null && Number(item.value) > 0))

function updateMeasure(key: FixedMeasurementKey, raw: string): void {
  if (!raw.trim()) {
    props.fixedMeasurements[key] = null
    return
  }
  const parsed = Number(raw)
  props.fixedMeasurements[key] = Number.isFinite(parsed) ? Math.round(parsed * 100) / 100 : props.fixedMeasurements[key]
}

function formatCm(value?: number | null): string {
  return value == null ? '—' : `${(value / 10).toFixed(2)} cm`
}

/** Centimetre value as shown in the read-only preview. */
function formatMeasure(value?: number | null): string {
  if (value == null || !Number.isFinite(Number(value))) return '—'
  return String(Number(Number(value).toFixed(2)))
}
</script>

<template>
  <section class="structure-card">
    <div class="structure-heading">
      <div>
        <h3>{{ $t('engineering.passportStructure.finishedDimensions') }}</h3>
        <p>{{ $t('engineering.passportStructure.measurementUnit') }}</p>
      </div>
    </div>
    <div class="dimension-layout">
      <SockMeasurementDrawing
        class="dimension-drawing"
        :dimensions="dimensions"
        :sock-type="sockType"
      />
      <div class="dimension-column">
        <div class="dimension-table">
          <div class="table-head">
            <strong>{{ $t('engineering.passportForm.code') }}</strong>
            <strong>{{ $t('engineering.passportStructure.dimension') }}</strong>
            <strong>{{ $t('engineering.passportStructure.tolerance') }}</strong>
            <strong>{{ $t('engineering.passportStructure.measurementValue') }}</strong>
            <strong>{{ $t('engineering.passportStructure.unit') }}</strong>
          </div>
          <div v-for="field in rows" :key="field.key" class="dimension-row">
            <strong class="dimension-code" :class="`code-${field.code.toLowerCase()}`">{{ field.code }}</strong>
            <div class="dimension-copy">
              <strong>{{ field.label }}</strong>
              <small>{{ field.measurement }}</small>
            </div>
            <span class="dimension-tolerance">{{ field.tolerance }}</span>
            <span v-if="readonly" class="dimension-value">{{ formatMeasure(field.value) }}</span>
            <label v-else class="dimension-input">
              <input
                :value="field.value ?? ''"
                min="0"
                step="0.1"
                type="number"
                @input="updateMeasure(field.key, ($event.target as HTMLInputElement).value)"
              >
            </label>
            <span class="dimension-unit">cm</span>
          </div>
        </div>
        <div v-if="derived.length" class="derived-strip">
          <strong>{{ $t('engineering.passportStructure.derivedValues') }}</strong>
          <span v-for="item in derived" :key="item.key">{{ item.label }}: {{ formatCm(item.value) }}</span>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.structure-card{padding:18px;border:1px solid #d7dfdc;background:#fff}
.structure-heading{display:flex;justify-content:space-between;margin-bottom:12px}
.structure-heading h3{margin:0}
.structure-heading p{margin:3px 0 0;color:#66726e;font-size:12px}
.dimension-layout{display:grid;grid-template-columns:minmax(300px,.95fr) minmax(0,1.15fr);gap:16px;align-items:start}
.dimension-drawing{margin:0}
.dimension-column{display:grid;gap:12px;min-width:0}
.dimension-table{overflow:hidden;border:1px solid #d7dfdc}
.table-head,.dimension-row{display:grid;grid-template-columns:42px minmax(0,1fr) 62px 92px 32px;gap:10px;align-items:center;padding:9px 11px}
.table-head{background:#f3f6f5;color:#53625d;font-size:11px;text-transform:uppercase}
.dimension-row{border-top:1px solid #e1e7e5}
.dimension-code{font-size:15px;font-weight:900;color:#173454}
.dimension-copy{display:grid;gap:2px;min-width:0}
.dimension-copy strong{color:#173454;font-size:13px;line-height:1.3}
.dimension-copy small{color:#66726e;font-size:11px;line-height:1.3}
.dimension-tolerance,.dimension-unit{color:#53625d;font-size:12px;font-weight:700}
.dimension-input input{width:100%;padding:7px 8px;border:1px solid #b9c5c1;border-radius:4px;background:#eef8e7;font:inherit}
.dimension-value{display:block;padding:7px 8px;border:1px solid transparent;color:#173454;font:inherit;font-weight:700;font-variant-numeric:tabular-nums}
.code-a{color:#2f6fb5}
.code-c{color:#2e8b57}
.code-d{color:#0f8b8d}
.code-e{color:#7b4fa0}
.code-f{color:#a15b2c}
.code-g{color:#c0392b}
.code-i,.code-j{color:#53625d}
.derived-strip{display:flex;align-items:center;gap:12px;flex-wrap:wrap;padding:10px 12px;border:1px dashed #c6d5e5;background:#f7fafd;color:#31433e;font-size:12px}
.derived-strip strong{font-size:11px;letter-spacing:.04em;text-transform:uppercase;color:#53625d}
@media(max-width:1100px){
  .dimension-layout{grid-template-columns:1fr}
}
@media(max-width:900px){
  .table-head{display:none}
  .dimension-row{grid-template-columns:42px 1fr;gap:6px 10px}
  .dimension-copy{grid-column:2}
  .dimension-tolerance,.dimension-input,.dimension-value,.dimension-unit{grid-column:2}
}
</style>
